import { useTranslation } from "react-i18next";
import GridDX from "../../../components/layout/griddx";
import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import useReportService from "../../../shared/services/reportservice";
import { useAuthContext } from "../../../context/authcontext";
import { useNotificationContext } from "../../../context/notificationcontext";
import SelectListDX from "../../../components/controls/selectlistdx";
import ListPageDX from "../../../components/business/listpagedx";
import { useOutletContext } from "react-router-dom";
import TextFieldDX from "../../../components/controls/textfielddx";
import AddEditEmbeddedModal from "../../../components/units_controls/addeditembeddedmodal";
import useCustomerService from "../../../shared/services/customerservice";
import useWarehouseService from "../../../shared/services/warehouseservice";
import { useTenantContext } from "../../../context/tenantcontext";
import UnitsDatePicker from "../../../components/units_controls/units_datepicker";
import moment from "moment";
import { API_DATE_FORMAT, DATE_FORMAT } from "../../../shared/globals";
import TypeTranslator from "../../../shared/typetranslator";
import i18n from "../../../components/multilingual/i18n";

const StockReport = () => {
	const { setShowTitle, setShowAddButton, setAddRecordfn, setAddRecordLabel } =
		useOutletContext() as any;
	const { t } = useTranslation();
	const { tenantId } = useTenantContext();
	const { unitTypes, stockMovementTypes, stockMovementReasons } =
		TypeTranslator();
	const [warehouses, setWarehouses] = useState<any>([]);
	const { getCustomers } = useCustomerService();
	const { getWarehouses } = useWarehouseService();
	const { getStockInOutReportForCustomer } = useReportService();
	const { userData } = useAuthContext();

	const [rows, setRows] = useState<any[]>([]);
	const [reportGenerated, setReportGenerated] = useState(false);
	const [customers, setCustomers] = useState<{ text: string; value: string }[]>(
		[]
	);
	const { setError } = useNotificationContext();
	const [isLoading, setIsLoading] = useState(false);
	const columns: GridColDef[] = [
		{ field: "stockMovementType", headerName: t("Report Type") },
		{ field: "movementReason", headerName: t("Movement Reason") },
		{ field: "skuCode", headerName: t("SKU Code") },
		{ field: "skuName", headerName: t("Name") },
		{ field: "quantity", headerName: t("Quantity") },
		{ field: "unitOfMeasurement", headerName: t("Unit") },
		{ field: "source", headerName: t("Source") },
		{ field: "runningBalance", headerName: t("Running Balance") },
		{ field: "warehouseCode", headerName: t("Warehouse Code") },
		{ field: "remarks", headerName: t("Remarks") },
		{ field: "createdDate", headerName: t("Created Date") },
	];

	const getDropDownData = () => {
		const p1 =
			userData?.userType === "S" ? getWarehouses(tenantId) : getWarehouses();
		const p2 =
			userData?.userType !== "C"
				? userData?.userType === "S"
					? getCustomers(tenantId)
					: getCustomers()
				: null;
		Promise.all([p1, p2]).then(([warehouses, users]) => {
			setWarehouses(
				warehouses.map((warehouse: any) => ({
					text: warehouse.name,
					value: warehouse.warehouseId,
				}))
			);
			setCustomers(
				users?.map((user: any) => ({
					text: user.fullname,
					value: user.customerId,
				}))
			);
		});
	};

	useEffect(() => {
		setShowAddButton(false);
		getDropDownData();
	}, [tenantId]);

	const defaultValues = {
		startDate: null,
		endDate: null,
		customerId: userData.customerId,
		skuCode: "",
		skuName: "",
		warehouseId: null,
		movementType: 1,
		movementReason: 1,
	};

	const [reportFilters, setReportFilters] = useState(defaultValues);
	const [errors, setErrors] = useState<any>([]);

	const handleInputChange = (
		event:
			| React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
			| any
	) => {
		const { name, value } = event.target;
		if (name === "movementType") {
			setReportFilters((prev: any) => ({
				...prev,
				movementType: value,
				movementReason: value == 1 ? 1 : 2,
			}));
		} else {
			setReportFilters((prev: any) => ({
				...prev,
				[name]: value,
			}));
		}
		setErrors((prev: any) => ({
			...prev,
			[name]: undefined,
		}));
	};
	const handleDateChange = (value: any, name: any) => {
		setReportFilters((prev: any) => ({
			...prev,
			[name]: value,
		}));
		setErrors((prev: any) => ({
			...prev,
			[name]: undefined,
		}));
	};

	const validateForm = () => {
		const newErrors: any = {};
		// startDate and endDate and customerId are required
		if (!reportFilters.startDate) {
			newErrors["startDate"] = t("Start Date is required");
		}
		if (!reportFilters.endDate) {
			newErrors["endDate"] = t("End Date is required");
		}
		if (
			reportFilters.startDate &&
			reportFilters.endDate &&
			moment(reportFilters.startDate).isAfter(moment(reportFilters.endDate))
		) {
			newErrors["endDate"] = t("End Date cannot be before Start Date");
		}
		if (!reportFilters.customerId) {
			newErrors["customerId"] = t("Customer is required");
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleGenerateReport = async () => {
		if (!validateForm()) return;
		setIsLoading(true);
		getStockInOutReportForCustomer({
			...reportFilters,

			startDate: moment(reportFilters.startDate).format(API_DATE_FORMAT),
			endDate: moment(reportFilters.endDate).format(API_DATE_FORMAT),
		})
			.then((result) => {
				const rowsFormatted = formattedRows(result);
				if (rowsFormatted.length === 0) {
					if (reportFilters.movementType === 1) {
						setError(t("No GRN created at this period."));
					} else if (reportFilters.movementType === 2) {
						setError(t("No Orders created at this period."));
					}
				} else {
					setRows(rowsFormatted);
					setReportGenerated(true);
				}
			})
			.catch((err) => setError(err))
			.finally(() => {
				setIsLoading(false);
			});
	};

	const formattedRows = (result: any) => {
		if (Array.isArray(result)) {
			return result.map((row: any) => ({
				...row,
				createdDate: row.createdDate
					? moment(row.createdDate).format(DATE_FORMAT)
					: "",
				stockMovementType: stockMovementTypes.find(
					(type) => type.value === row.movementType
				)?.text,
				unitOfMeasurement: unitTypes[row.uom].text,
			}));
		}
		return [];
	};

	useEffect(() => {
		setRows(formattedRows(rows));
	}, [i18n.language]);

	return (
		<GridDX container>
			<GridDX item xs={12}>
				<AddEditEmbeddedModal
					isSaving={isLoading}
					isPrintable={false}
					saveAndSubmitLabel={t("Generate Report")}
					onSaveAndSubmit={handleGenerateReport}
					showSaveAndSubmit={true}
					showSaveButton={false}
				>
					<GridDX container columnSpacing={1} rowSpacing={2}>
						{userData.userType !== "C" ? (
							<GridDX item xs={12} md={4}>
								<SelectListDX
									label={t("Customer")}
									name="customerId"
									value={reportFilters.customerId}
									onChange={handleInputChange}
									error={errors["customerId"]}
									items={customers}
									InputLabelProps={{
										shrink: reportFilters.customerId !== null, // Set to true if there's a value just to handle label position
									}}
								/>
							</GridDX>
						) : (
							<GridDX item xs={12} md={4}>
								<TextFieldDX
									disabled
									label={t("Customer")}
									value={userData.fullName}
								/>
							</GridDX>
						)}
						<GridDX item xs={12} md={4}>
							<UnitsDatePicker
								label={t("Start Date")}
								name="startDate"
								value={moment(reportFilters.startDate)}
								onChange={(value: any) => handleDateChange(value, "startDate")}
								error={errors["startDate"]}
							/>
						</GridDX>
						<GridDX item xs={12} md={4}>
							<UnitsDatePicker
								label={t("End Date")}
								name="endDate"
								value={moment(reportFilters.endDate)}
								onChange={(value: any) => handleDateChange(value, "endDate")}
								error={errors["endDate"]}
								maxDate={moment().endOf("day")}
							/>
						</GridDX>
						<GridDX item xs={12} md={4}>
							<TextFieldDX
								label={t("SKU Code")}
								name="skuCode"
								value={reportFilters.skuCode}
								onChange={handleInputChange}
								error={errors["skuCode"]}
							/>
						</GridDX>
						<GridDX item xs={12} md={4}>
							<TextFieldDX
								label={t("SKU Name")}
								name="skuName"
								value={reportFilters.skuName}
								onChange={handleInputChange}
								error={errors["skuName"]}
							/>
						</GridDX>

						<GridDX item xs={12} md={4}>
							<SelectListDX
								label={t("Warehouse")}
								name="warehouseId"
								value={reportFilters.warehouseId}
								onChange={handleInputChange}
								error={errors["warehouseId"]}
								items={warehouses}
								InputLabelProps={{
									shrink: reportFilters.warehouseId !== null, // Set to true if there's a value just to handle label position
								}}
							/>
						</GridDX>
						<GridDX item xs={12} md={4}>
							<SelectListDX
								label={t("Movement Type")}
								name="movementType"
								value={reportFilters.movementType}
								onChange={handleInputChange}
								error={errors["movementType"]}
								items={stockMovementTypes}
								InputLabelProps={{
									shrink: reportFilters.movementType !== null,
								}}
							/>
						</GridDX>
						<GridDX item xs={12} md={4}>
							<SelectListDX
								label={t("Movement Reason")}
								name="movementReason"
								value={reportFilters.movementReason}
								onChange={handleInputChange}
								error={errors["movementReason"]}
								items={stockMovementReasons}
								InputLabelProps={{
									shrink: reportFilters.movementReason !== null,
								}}
								disabled
							/>
						</GridDX>
					</GridDX>
				</AddEditEmbeddedModal>
			</GridDX>
			{reportGenerated && (
				<GridDX item xs={12}>
					<ListPageDX
						getRowId={(row: any) => row.stockLedgerId}
						listTitle={t("Stock Movement Report")}
						gridHeading={t("Generated Report")}
						name={t("SKU")}
						rows={Array.isArray(rows) ? rows : []}
						columns={columns}
						showAddButton={false}
						searchAndFilter={false}
						exportToPDF={false}
						exportToCSV={false}
						exportDropdownLabel={t("Download Report")}
						exportDropdownActionsLabels={[
							t("Export to PDF"),
							t("Export to CSV"),
						]}
					/>
				</GridDX>
			)}
		</GridDX>
	);
};

export default StockReport;
